import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowDownRight,
  ArrowUpRight,
  Calendar,
  CreditCard,
  Download,
  Filter,
  MapPin,
  Search,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/earnings")({
  head: () => ({
    meta: [
      { title: "Financial Earnings — Spayzo Admin" },
      {
        name: "description",
        content: "Platform revenue, payouts and commission overview.",
      },
    ],
  }),
  component: FinancialEarnings,
});

const PERIODS = ["Today", "7D", "30D", "3M", "YTD", "Custom"] as const;
type PeriodKey = (typeof PERIODS)[number];
const PERIOD_I18N: Record<PeriodKey, string> = {
  Today: "today",
  "7D": "7d",
  "30D": "30d",
  "3M": "3m",
  YTD: "ytd",
  Custom: "custom",
};

const KPI_DEFS = [
  { key: "totalRevenue", value: <Money value="412,890" />, delta: "+18.4%", trend: "up" as const, hintKey: "vsLastMonth", icon: TrendingUp, tint: "#5958F3" },
  { key: "commissionEarned", value: <Money value="61,933" />, delta: "+22.1%", trend: "up" as const, hintKey: "avgRate", icon: Wallet, tint: "#22C55E" },
  { key: "totalPayouts", value: <Money value="326,710" />, delta: "+14.2%", trend: "up" as const, hintKey: "disbursed", icon: CreditCard, tint: "#F59E0B" },
  { key: "subscriptionRevenue", value: <Money value="24,247" />, delta: "+9.6%", trend: "up" as const, hintKey: "activePlans", icon: TrendingUp, tint: "#EC4899" },
  { key: "pendingPayouts", value: <Money value="18,450" />, deltaCount: 12, trend: "down" as const, hintKey: "waitingApproval", icon: MapPin, tint: "#EF4444" },
] as const;


const weeklyRevenue = [
  { period: "Week 1", Gross: 92000, Commission: 13800 },
  { period: "Week 2", Gross: 104500, Commission: 15675 },
  { period: "Week 3", Gross: 118200, Commission: 17730 },
  { period: "Week 4", Gross: 98190, Commission: 14728 },
];

const dailyRevenue = [
  { period: "Jun 22", Gross: 12400, Commission: 1860 },
  { period: "Jun 23", Gross: 14100, Commission: 2115 },
  { period: "Jun 24", Gross: 16200, Commission: 2430 },
  { period: "Jun 25", Gross: 15100, Commission: 2265 },
  { period: "Jun 26", Gross: 18300, Commission: 2745 },
  { period: "Jun 27", Gross: 21400, Commission: 3210 },
  { period: "Jun 28", Gross: 20690, Commission: 3103 },
];

const breakdown = [
  { name: "Booking Fees", value: 52, color: "#5958F3" },
  { name: "Subscriptions", value: 20, color: "#22C55E" },
  { name: "Commissions", value: 15, color: "#F59E0B" },
  { name: "Add-ons & Menus", value: 13, color: "#EC4899" },
];

type TxnStatus = "Completed" | "Paid" | "Sent" | "Settled" | "Pending";
type TxnType = "Booking" | "Subscription" | "Payout" | "Commission" | "Add-on";

const transactions: Array<{
  id: string;
  desc: string;
  merchant: string;
  type: TxnType;
  date: string;
  status: TxnStatus;
  amount: number;
}> = [
  { id: "TXN-9847", desc: "Booking — Space A", merchant: "Workspace Hub", type: "Booking", date: "Jun 29, 2025", status: "Completed", amount: 1200 },
  { id: "TXN-9846", desc: "Monthly subscription", merchant: "Studio Serene", type: "Subscription", date: "Jun 29, 2025", status: "Paid", amount: 299 },
  { id: "TXN-9845", desc: "Payout disbursement", merchant: "Zen Collective", type: "Payout", date: "Jun 28, 2025", status: "Sent", amount: -8400 },
  { id: "TXN-9844", desc: "Commission split", merchant: "Flow Lounge", type: "Commission", date: "Jun 28, 2025", status: "Settled", amount: 1875 },
  { id: "TXN-9843", desc: "Menu item sale", merchant: "The Rooftop", type: "Add-on", date: "Jun 27, 2025", status: "Pending", amount: 450 },
];

const topMerchants = [
  { rank: 1, name: "The Workspace Hub", location: "Business Bay", bookings: 142, delta: "+22%", revenue: <Money value="42,800" /> },
  { rank: 2, name: "Studio Serene", location: "DIFC", bookings: 118, delta: "+15%", revenue: <Money value="34,150" /> },
  { rank: 3, name: "Zen Collective", location: "JLT", bookings: 97, delta: "+11%", revenue: <Money value="28,900" /> },
  { rank: 4, name: "Flow Lounge", location: "Marina", bookings: 84, delta: "+8%", revenue: <Money value="22,400" /> },
  { rank: 5, name: "The Rooftop", location: "Downtown", bookings: 76, delta: "+5%", revenue: <Money value="19,750" /> },
];

const initialPayouts = [
  { id: "p1", merchant: "The Workspace Hub", requested: "Jun 28", method: "Bank Transfer", account: "****4291", amount: 6200 },
  { id: "p2", merchant: "Studio Serene", requested: "Jun 28", method: "Stripe", account: "****7813", amount: 4850 },
  { id: "p3", merchant: "Zen Collective", requested: "Jun 27", method: "Bank Transfer", account: "****2234", amount: 3900 },
  { id: "p4", merchant: "Flow Lounge", requested: "Jun 27", method: "Stripe", account: "****9120", amount: 2150 },
  { id: "p5", merchant: "The Rooftop", requested: "Jun 26", method: "Bank Transfer", account: "****5567", amount: 1350 },
];

const STATUS_STYLE: Record<TxnStatus, string> = {
  Completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  Paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  Sent: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400",
  Settled: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400",
  Pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
};

const TYPE_STYLE: Record<TxnType, string> = {
  Booking: "bg-[#5958F3]/10 text-[#5958F3]",
  Subscription: "bg-emerald-500/10 text-emerald-600",
  Payout: "bg-amber-500/10 text-amber-600",
  Commission: "bg-violet-500/10 text-violet-600",
  "Add-on": "bg-pink-500/10 text-pink-600",
};

function currency(n: number) {
  const sign = n < 0 ? "-" : "+";
  return `${sign}SAR ${Math.abs(n).toLocaleString()}`;
}

function FinancialEarnings() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<PeriodKey>("30D");
  const [chartMode, setChartMode] = useState<"week" | "day">("week");
  const [search, setSearch] = useState("");
  const [payouts, setPayouts] = useState(initialPayouts);
  const [confirm, setConfirm] = useState<{ open: boolean; id?: string; all?: boolean }>({
    open: false,
  });

  const chartData = chartMode === "week" ? weeklyRevenue : dailyRevenue;

  const filteredTxns = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter(
      (tx) =>
        tx.id.toLowerCase().includes(q) ||
        tx.desc.toLowerCase().includes(q) ||
        tx.merchant.toLowerCase().includes(q),
    );
  }, [search]);

  const totalPending = payouts.reduce((sum, p) => sum + p.amount, 0);

  const approvePayout = (id: string) => {
    const target = payouts.find((p) => p.id === id);
    setPayouts((prev) => prev.filter((p) => p.id !== id));
    toast.success(
      t("pages.admin.earnings.toasts.approved", {
        amount: target?.amount.toLocaleString() ?? "",
        merchant: target?.merchant ?? "",
      }),
    );
    setConfirm({ open: false });
  };

  const approveAll = () => {
    toast.success(
      t("pages.admin.earnings.toasts.processedAll", {
        count: payouts.length,
        total: totalPending.toLocaleString(),
      }),
    );
    setPayouts([]);
    setConfirm({ open: false });
  };

  const BREAKDOWN_KEYS: Record<string, string> = {
    "Booking Fees": "bookingFees",
    Subscriptions: "subscriptions",
    Commissions: "commissions",
    "Add-ons & Menus": "addOns",
  };


  return (
    <AdminShell
      title={t("pages.admin.earnings.title")}
      subtitle={t("pages.admin.earnings.subtitle")}
      actions={
        <>
          <Button
            variant="outline"
            className="h-10 gap-2 rounded-xl"
            onClick={() => toast.success(t("pages.admin.earnings.exportQueued"))}
          >
            <Download className="h-4 w-4" />
            {t("pages.admin.earnings.exportReport")}
          </Button>
          <Button
            className="h-10 gap-2 rounded-xl bg-[#5958F3] shadow-soft hover:bg-[#5958F3]/90"
            onClick={() => setConfirm({ open: true, all: true })}
            disabled={payouts.length === 0}
          >
            <Wallet className="h-4 w-4" />
            {t("pages.admin.earnings.processPayouts")}
          </Button>
        </>
      }
    >
      {/* Toolbar */}
      <Card className="mb-6 rounded-2xl border-border/60 p-4 shadow-soft">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex h-10 items-center gap-2 rounded-xl border border-border/60 bg-background px-3 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t("pages.admin.earnings.dateRange")}</span>
            </div>
            <Tabs value={period} onValueChange={(v) => setPeriod(v as PeriodKey)}>
              <TabsList className="h-10 rounded-xl bg-muted p-1">
                {PERIODS.map((p) => (
                  <TabsTrigger
                    key={p}
                    value={p}
                    className="rounded-lg px-3 text-xs font-medium"
                  >
                    {t(`pages.admin.earnings.periods.${PERIOD_I18N[p]}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("pages.admin.earnings.searchPlaceholder")}
                className="h-10 w-64 rounded-xl ps-9"
              />
            </div>
            <Button variant="outline" className="h-10 gap-2 rounded-xl">
              <Filter className="h-4 w-4" />
              {t("pages.admin.earnings.filters")}
            </Button>
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {KPI_DEFS.map((k) => (
          <Card
            key={k.key}
            className="relative overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <span
              className="absolute inset-x-0 top-0 h-1"
              style={{ backgroundColor: k.tint }}
            />
            <div className="flex items-center justify-between">
              <span
                className="grid h-10 w-10 place-items-center rounded-xl"
                style={{ backgroundColor: `${k.tint}1a`, color: k.tint }}
              >
                <k.icon className="h-5 w-5" />
              </span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  k.trend === "up"
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
                    : "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
                )}
              >
                {k.trend === "up" ? (
                  <ArrowUpRight className="h-3 w-3" />
                ) : (
                  <ArrowDownRight className="h-3 w-3" />
                )}
                {"delta" in k
                  ? k.delta
                  : t("pages.admin.earnings.kpis.pendingDelta", { count: k.deltaCount })}
              </span>
            </div>
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t(`pages.admin.earnings.kpis.${k.key}`)}
            </p>
            <p className="mt-1 font-display text-2xl font-bold tracking-tight">{k.value}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t(`pages.admin.earnings.kpis.hints.${k.hintKey}`)}
            </p>
          </Card>
        ))}
      </div>


      {/* Charts */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("pages.admin.earnings.charts.revenueOverTime")}</p>
              <p className="text-xs text-muted-foreground">
                {t("pages.admin.earnings.charts.revenueOverTimeSub")}
              </p>
            </div>
            <Tabs value={chartMode} onValueChange={(v) => setChartMode(v as "week" | "day")}>
              <TabsList className="h-9 rounded-xl bg-muted p-1">
                <TabsTrigger
                  value="week"
                  className="rounded-lg px-3 text-xs"
                >
                  {t("pages.admin.earnings.charts.byWeek")}
                </TabsTrigger>
                <TabsTrigger
                  value="day"
                  className="rounded-lg px-3 text-xs"
                >
                  {t("pages.admin.earnings.charts.byDay")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={60}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
                />
                <RTooltip
                  cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                  formatter={(v: number) => [`SAR ${v.toLocaleString()}`, ""]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid hsl(var(--border))",
                    fontSize: 12,
                  }}
                />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ paddingTop: 12, fontSize: 12 }}
                  formatter={(value) => <span className="ps-3">{value}</span>}
                />
                <Bar
                  dataKey="Gross"
                  fill="#5958F3"
                  radius={[6, 6, 0, 0]}
                  name={t("pages.admin.earnings.charts.grossRevenue")}
                />
                <Bar
                  dataKey="Commission"
                  fill="#22C55E"
                  radius={[6, 6, 0, 0]}
                  name={t("pages.admin.earnings.charts.commission")}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="mb-2">
            <p className="text-sm font-semibold">{t("pages.admin.earnings.charts.breakdown")}</p>
            <p className="text-xs text-muted-foreground">
              {t("pages.admin.earnings.charts.breakdownSub")}
            </p>
          </div>
          <div className="relative h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={breakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={62}
                  outerRadius={92}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {breakdown.map((e) => (
                    <Cell key={e.name} fill={e.color} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(v: number, n: string) => [
                    `${v}%`,
                    t(`pages.admin.earnings.breakdown.${BREAKDOWN_KEYS[n] ?? "bookingFees"}`),
                  ]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid hsl(var(--border))",
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <p className="font-display text-xl font-bold">412K</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("pages.admin.earnings.charts.totalSar")}
              </p>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {breakdown.map((b) => (
              <div key={b.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: b.color }} />
                  <span className="text-muted-foreground">
                    {t(`pages.admin.earnings.breakdown.${BREAKDOWN_KEYS[b.name] ?? "bookingFees"}`)}
                  </span>
                </div>
                <span className="font-semibold">{b.value}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>


      {/* Transactions */}
      <Card className="mt-6 rounded-2xl border-border/60 shadow-soft">
        <div className="flex items-center justify-between p-5">
          <div>
            <p className="text-sm font-semibold">{t("pages.admin.earnings.transactions.title")}</p>
            <p className="text-xs text-muted-foreground">
              {t("pages.admin.earnings.transactions.sub")}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-lg text-[#5958F3] hover:bg-[#5958F3]/10 hover:text-[#5958F3]"
            onClick={() => toast.info(t("pages.admin.earnings.transactions.opening"))}
          >
            {t("pages.admin.earnings.transactions.viewAll")}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-y border-border/60 bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-surface">
                <TableHead className="px-5 py-2.5">{t("pages.admin.earnings.transactions.cols.transaction")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("pages.admin.earnings.transactions.cols.merchant")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("pages.admin.earnings.transactions.cols.type")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("pages.admin.earnings.transactions.cols.date")}</TableHead>
                <TableHead className="px-5 py-2.5">{t("pages.admin.earnings.transactions.cols.status")}</TableHead>
                <TableHead className="px-5 py-2.5 text-end">{t("pages.admin.earnings.transactions.cols.amount")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTxns.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="px-5 py-10 text-center text-sm text-muted-foreground"
                  >
                    {t("pages.admin.earnings.transactions.empty")}
                  </TableCell>
                </TableRow>
              )}
              {filteredTxns.map((tx) => (
                <TableRow
                  key={tx.id}
                  className="border-b border-border/40 transition-colors last:border-b-0 hover:bg-surface/50"
                >
                  <TableCell className="px-5 py-3">
                    <p className="font-semibold">#{tx.id}</p>
                    <p className="text-xs text-muted-foreground">{tx.desc}</p>
                  </TableCell>
                  <TableCell className="px-5 py-3 text-sm">{tx.merchant}</TableCell>
                  <TableCell className="px-5 py-3">
                    <span
                      className={cn(
                        "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
                        TYPE_STYLE[tx.type],
                      )}
                    >
                      {t(`pages.admin.earnings.txTypes.${tx.type}`)}
                    </span>
                  </TableCell>
                  <TableCell className="px-5 py-3 text-sm text-muted-foreground">{tx.date}</TableCell>
                  <TableCell className="px-5 py-3">
                    <Badge
                      variant="secondary"
                      className={cn(
                        "rounded-full text-[11px] font-semibold",
                        STATUS_STYLE[tx.status],
                      )}
                    >
                      {t(`pages.admin.earnings.txStatus.${tx.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className={cn(
                      "px-5 py-3 text-end font-display font-semibold",
                      tx.amount < 0 ? "text-rose-600" : "text-emerald-600",
                    )}
                  >
                    {currency(tx.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Top merchants + pending payouts */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("pages.admin.earnings.topMerchants.title")}</p>
              <p className="text-xs text-muted-foreground">
                {t("pages.admin.earnings.topMerchants.sub")}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-lg text-[#5958F3] hover:bg-[#5958F3]/10 hover:text-[#5958F3]"
              onClick={() => toast.info(t("pages.admin.earnings.topMerchants.opening"))}
            >
              {t("pages.admin.earnings.topMerchants.viewAll")}
            </Button>
          </div>
          <div className="space-y-2">
            {topMerchants.map((m) => (
              <div
                key={m.rank}
                className="flex items-center gap-3 rounded-xl border border-border/50 bg-surface/50 p-3 transition-colors hover:border-[#5958F3]/40"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#5958F3]/10 font-display text-sm font-bold text-[#5958F3]">
                  {m.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{m.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {m.location} · {t("pages.admin.earnings.topMerchants.bookings", { count: m.bookings })}
                  </p>
                </div>
                <div className="text-end">
                  <p className="font-display text-sm font-bold">{m.revenue}</p>
                  <p className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600">
                    <ArrowUpRight className="h-3 w-3" />
                    {m.delta}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("pages.admin.earnings.queue.title")}</p>
              <p className="text-xs text-muted-foreground">
                {t("pages.admin.earnings.queue.sub", {
                  total: totalPending.toLocaleString(),
                })}
              </p>
            </div>
            <Button
              size="sm"
              className="h-9 rounded-lg bg-[#5958F3] hover:bg-[#5958F3]/90"
              disabled={payouts.length === 0}
              onClick={() => setConfirm({ open: true, all: true })}
            >
              {t("pages.admin.earnings.queue.processAll")}
            </Button>
          </div>
          <div className="space-y-2">
            {payouts.length === 0 && (
              <div className="rounded-xl border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
                {t("pages.admin.earnings.queue.empty")}
              </div>
            )}
            {payouts.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 rounded-xl border border-border/50 bg-surface/50 p-3"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-amber-500/10 text-amber-600">
                  <Wallet className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{p.merchant}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {t("pages.admin.earnings.queue.requested", {
                      when: p.requested,
                      method: p.method,
                      account: p.account,
                    })}
                  </p>
                </div>
                <p className="font-display text-sm font-bold"><Riyal /> {p.amount.toLocaleString()}</p>
                <Button
                  size="sm"
                  className="h-8 rounded-lg bg-[#5958F3] hover:bg-[#5958F3]/90"
                  onClick={() => setConfirm({ open: true, id: p.id })}
                >
                  {t("pages.admin.earnings.queue.approve")}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <AlertDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm((c) => ({ ...c, open: o }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm.all
                ? t("pages.admin.earnings.confirm.allTitle")
                : t("pages.admin.earnings.confirm.singleTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm.all
                ? t("pages.admin.earnings.confirm.allDesc", {
                    total: totalPending.toLocaleString(),
                    count: payouts.length,
                  })
                : t("pages.admin.earnings.confirm.singleDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("pages.admin.earnings.confirm.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#5958F3] hover:bg-[#5958F3]/90"
              onClick={() =>
                confirm.all ? approveAll() : confirm.id && approvePayout(confirm.id)
              }
            >
              {t("pages.admin.earnings.confirm.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>

  );
}
